import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { verifyEmailToken } from "@/lib/api";
import { track } from "@/lib/analytics";
import { CheckCircle2, AlertTriangle } from "lucide-react";

export default function VerifyEmail() {
    const { token } = useParams();
    const [state, setState] = useState("loading"); // loading | success | error
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await verifyEmailToken(token);
                if (!cancelled) {
                    setState("success");
                    track("email_verified");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err?.response?.data?.detail ||
                            "This verification link is invalid or has expired.",
                    );
                    setState("error");
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token]);

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div
                    className="bl-container py-20 max-w-md text-center"
                    data-testid="verify-email-page"
                >
                    {state === "loading" && (
                        <>
                            <div className="bl-spinner mx-auto mb-4" />
                            <p className="text-bl-muted">
                                Verifying your email…
                            </p>
                        </>
                    )}
                    {state === "success" && (
                        <div data-testid="verify-success">
                            <div className="mx-auto w-14 h-14 rounded-full bg-bl-primary/10 flex items-center justify-center">
                                <CheckCircle2 className="w-7 h-7 text-bl-primary" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                                Email verified.
                            </h1>
                            <p className="mt-3 text-bl-muted leading-relaxed">
                                Thanks for confirming. Your account is fully
                                set up.
                            </p>
                            <Link
                                to="/dashboard"
                                className="bl-btn-primary mt-8 inline-flex"
                                data-testid="verify-success-cta"
                            >
                                Go to dashboard
                            </Link>
                        </div>
                    )}
                    {state === "error" && (
                        <div data-testid="verify-error">
                            <div className="mx-auto w-14 h-14 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center">
                                <AlertTriangle className="w-7 h-7 text-rose-700" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl text-bl">
                                Couldn't verify.
                            </h1>
                            <p className="mt-3 text-bl-muted leading-relaxed">
                                {error}
                            </p>
                            <p className="mt-6 text-sm text-bl-muted">
                                You can request a new verification email from
                                your{" "}
                                <Link
                                    to="/account"
                                    className="text-bl-primary hover:underline"
                                >
                                    account settings
                                </Link>
                                .
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
